import { Link } from '@tanstack/react-router';
import { useQueries } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Sparkles } from 'lucide-react';

import { Hint } from '../../../chrome/hint';
import { EmptyState, ErrorState, LoadingState, QueryStates } from '../../../chrome/state';
import { mt } from '../../../chrome/measure/messages';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Card, CardContent } from '../../../ui/card';
import type { UseQueryResult } from '@tanstack/react-query';
import type { ApiFailureError } from '../../../lib/http/errors';
import { useApiClient } from '../../session/client';
import {
  compositionQueryOptions,
  useAmplification,
  useComments,
  useDigest,
  usePostMetrics,
  useSentiment,
  useTerms,
  type Window,
} from '../api/queries';
import type { Composition, PostReach, StanceCounts } from '../api/schemas';
import type { ApiFailure } from '../../../lib/http/errors';
import { readingOf, scoreOf } from '../publications/readings';
import { AmplifiersBody } from './AmplifiersBody';
import { CommentsBody } from './CommentsBody';
import {
  DIMENSIONS,
  settleDisclosure,
  topAmplifiers,
  type Dimension,
  type Disclosure,
  type Reading,
} from './compute';
import { DigestBody } from './DigestBody';
import { DimensionBody } from './DimensionBody';
import { Frame } from './Frame';
import { historyOf, MetricHistory, MetricsGrid } from './MetricsGrid';
import { pub } from './messages';
import { ALL_COMMENTS, useAllComments } from './search';
import { TermsBody } from './TermsBody';

export interface Ctx {
  campaignId: string;
  tenantId: string;
  postId: string;
  window: Window;
}

const COMMENTS_PAGE = 5;

const COMMENTS_ALL_PAGE = 50;

/** The reading-window comments, shared by the figure row's count and the
 *  comment card so both read one request. */
function useWindowComments(ctx: Ctx, all: boolean) {
  const { campaignId, tenantId, postId, window } = ctx;
  return useComments(campaignId, tenantId, postId, {
    window,
    limit: all ? COMMENTS_ALL_PAGE : COMMENTS_PAGE,
  });
}

/**
 * The window the narrowing basis is read over. `get_composition` takes no
 * window, so its cells count the post's whole audience; the basis they are
 * narrowed against has to count the post's whole comment population too, or a
 * reader who moves `?from=&to=` watches a cell appear under one window and
 * vanish under the next and reads its count off the pair.
 *
 * ASSUMED: this asks for a window wider than any reading window a reader can
 * set, and `read_post_comments` builds its window without validating the span.
 */
export const WHOLE_POST: Window = {
  from: '1970-01-01T00:00:00.000Z',
  to: '2999-12-31T23:59:59.999Z',
};

/** The comment population every cell is narrowed against. Nothing is drawn
 *  before it arrives: a composition rendered while the basis is missing is a
 *  composition narrowed against nothing. */
type Basis =
  | { kind: 'pending' }
  | { kind: 'failed'; failure: ApiFailure; retry: () => void }
  | { kind: 'ready'; counts: StanceCounts };

export function DigestSection(props: { ctx: Ctx }): ReactNode {
  const { campaignId, tenantId, postId, window } = props.ctx;
  const query = useDigest(campaignId, tenantId, postId, { window });
  return (
    <Frame title={pub('digest.title')} icon={<Sparkles aria-hidden className="size-4" />}>
      <QueryStates query={query} isEmpty={() => false} empty={null}>
        {(digest) => <DigestBody digest={digest} campaignId={campaignId} postId={postId} />}
      </QueryStates>
    </Frame>
  );
}

export function MetricsSection(props: { ctx: Ctx; figures: PostReach | null }): ReactNode {
  const { campaignId, tenantId, postId, window } = props.ctx;
  const sentiment = useSentiment(campaignId, tenantId, { scopeRef: postId, window });
  const comments = useWindowComments(props.ctx, useAllComments());
  return (
    <MetricsGrid
      figures={props.figures}
      sentiment={readingOf(sentiment, scoreOf)}
      comments={readingOf(comments, (data) => {
        const counts = data.pages[0]?.counts;
        return counts === undefined
          ? 0
          : counts.supportive + counts.hostile + counts.neutral + counts.off_topic;
      })}
    />
  );
}

export function MetricHistorySection(props: { ctx: Ctx }): ReactNode {
  const { campaignId, tenantId, postId, window } = props.ctx;
  const query = usePostMetrics(campaignId, tenantId, postId, { window });
  return (
    <Frame title={pub('history.title')}>
      <QueryStates
        query={query}
        isEmpty={(data) => historyOf(data.rows).series.length === 0}
        empty={<EmptyState title={pub('history.empty')} />}
      >
        {(data) => <MetricHistory rows={data.rows} />}
      </QueryStates>
    </Frame>
  );
}

export function TermsSection(props: { ctx: Ctx }): ReactNode {
  const { campaignId, tenantId, postId, window } = props.ctx;
  const query = useTerms(campaignId, tenantId, postId, { window });
  return (
    <Frame title={pub('terms.title')}>
      <QueryStates
        query={query}
        isEmpty={(data) => data.rows.length === 0}
        empty={<EmptyState title={pub('terms.empty')} />}
      >
        {(data) => <TermsBody rows={data.rows} />}
      </QueryStates>
    </Frame>
  );
}

function AmplifiersSection(props: { ctx: Ctx }): ReactNode {
  const { campaignId, tenantId, postId, window } = props.ctx;
  const query = useAmplification(campaignId, tenantId, postId, { window });
  return (
    <Frame
      title={pub('amp.title')}
      aside={<Hint label={pub('amp.about')}>{pub('amp.scope')}</Hint>}
    >
      <QueryStates
        query={query}
        isEmpty={(data) => topAmplifiers(data.rows, postId).length === 0}
        empty={<EmptyState title={pub('amp.empty')} />}
      >
        {(data) => (
          <AmplifiersBody rows={topAmplifiers(data.rows, postId)} campaignId={campaignId} />
        )}
      </QueryStates>
    </Frame>
  );
}

/** The readings this card may draw, or none: the disclosure decides, never the
 *  card. */
function disclosed(disclosure: Disclosure): readonly (Reading | undefined)[] {
  return disclosure.kind === 'disclosed' ? disclosure.readings : [];
}

function DimensionCard(props: {
  dimension: Dimension;
  index: number;
  query: UseQueryResult<Composition, ApiFailureError>;
  disclosure: Disclosure;
  basisError: ReactNode;
  commenters: number;
  ageBandTotal: number | null;
}): ReactNode {
  const { dimension, index, query, disclosure, basisError } = props;
  const title = pub(`dimension.${dimension}`);
  const reading = disclosed(disclosure)[index];
  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        {reading === undefined && <h3 className="text-title-sm">{title}</h3>}
        {disclosure.kind === 'asking' && <LoadingState />}
        {disclosure.kind === 'no-basis' && basisError}
        {disclosure.kind === 'disclosed' && (
          <QueryStates query={query} isEmpty={() => false} empty={null}>
            {() =>
              reading === undefined ? null : (
                <DimensionBody
                  dimension={dimension}
                  title={title}
                  reading={reading}
                  commenters={props.commenters}
                  ageBandTotal={props.ageBandTotal}
                />
              )
            }
          </QueryStates>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * The comment list and the dimension charts. They share one container because
 * the composition is narrowed against the post's comments: no composition cell
 * within one of its stance's count is rendered. Nothing is drawn before the
 * basis arrives, so a cell is never on screen before the comments that would
 * narrow it. Each dimension is read on its own, from commenters only: nothing
 * records which values one person held together, and likers are not profiled.
 */
export function AudienceSection(props: { ctx: Ctx }): ReactNode {
  const { campaignId, tenantId, postId } = props.ctx;
  const client = useApiClient();
  const all = useAllComments();
  const comments = useWindowComments(props.ctx, all);
  const population = useComments(campaignId, tenantId, postId, {
    window: WHOLE_POST,
    limit: 1,
  });
  const queries = useQueries({
    queries: DIMENSIONS.map((dimension) =>
      compositionQueryOptions(client, campaignId, tenantId, postId, {
        breakdownBy: [dimension],
        source: 'commenter',
      }),
    ),
  }) as UseQueryResult<Composition, ApiFailureError>[];

  const counts = population.data?.pages[0]?.counts;
  const basis: Basis =
    counts !== undefined
      ? { kind: 'ready', counts }
      : population.status === 'error'
        ? {
            kind: 'failed',
            failure: population.error.failure,
            retry: () => void population.refetch(),
          }
        : { kind: 'pending' };

  const disclosure = settleDisclosure(
    basis,
    DIMENSIONS.map((dimension, index) => ({
      dimension,
      status: queries[index]?.status ?? 'pending',
      composition: queries[index]?.data,
    })),
  );

  // The basis is the one failure a card renders rather than its own query's,
  // and its retry closure belongs here; which cards show it is the
  // disclosure's call.
  const basisError: ReactNode =
    basis.kind === 'failed' ? <ErrorState failure={basis.failure} onRetry={basis.retry} /> : null;

  const commenters =
    counts === undefined
      ? 0
      : counts.supportive + counts.hostile + counts.neutral + counts.off_topic;

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <div className="flex flex-col gap-6 lg:col-span-2">
        <Frame
          title={pub('comments.title')}
          aside={
            all ? null : (
              <Button asChild variant="outline" size="sm">
                <Link
                  to="."
                  search={(current: Record<string, unknown>) => ({ ...current, ...ALL_COMMENTS })}
                >
                  {pub('comments.all')}
                </Link>
              </Button>
            )
          }
        >
          <QueryStates
            query={comments}
            isEmpty={(data) => data.pages.every((page) => page.rows.length === 0)}
            empty={<EmptyState title={pub('comments.empty')} />}
          >
            {(data) => {
              const last = data.pages.at(-1);
              return last === undefined ? null : (
                <CommentsBody
                  rows={data.pages.flatMap((page) => page.rows)}
                  counts={last.counts}
                  hasMore={comments.hasNextPage}
                  loadingMore={comments.isFetchingNextPage}
                  onMore={() => void comments.fetchNextPage()}
                />
              );
            }}
          </QueryStates>
        </Frame>
        <section className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <h2 className="text-title-md">{pub('dimensions.title')}</h2>
              <Hint label={pub('dimensions.about')}>{pub('dimensions.description')}</Hint>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline">{pub('dimensions.sample')}</Badge>
              <Hint label={pub('dimensions.sampleAbout')}>{mt('withheld.biased')}</Hint>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {DIMENSIONS.map((dimension, index) => (
              <DimensionCard
                key={dimension}
                dimension={dimension}
                index={index}
                query={queries[index]!}
                disclosure={disclosure}
                basisError={basisError}
                commenters={commenters}
                ageBandTotal={disclosed(disclosure)[DIMENSIONS.indexOf('age_band')]?.total ?? null}
              />
            ))}
          </div>
        </section>
      </div>
      <div className="flex flex-col gap-6">
        <AmplifiersSection ctx={props.ctx} />
      </div>
    </div>
  );
}
